import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from "react";
import { Client } from "@stomp/stompjs";
import { pushNotificationService } from "../services/Notification";
import config from "../config";

const brokerURL = config.WEB_SOCKET_URL;
const rabbit_username = config.RABBITMQ_USERNAME;
const rabbit_password = config.RABBITMQ_PASSWORD;

export const MessageContext = createContext();

// Helper function to update app badge (can be used directly or via service worker)
const updateAppBadge = async (count) => {
  // First try direct Badging API (works when app is in foreground)
  if ('setAppBadge' in navigator) {
    try {
      if (count > 0) {
        await navigator.setAppBadge(count);
      } else {
        await navigator.clearAppBadge();
      }
      return true;
    } catch (error) {
      console.warn('Direct badge update failed, trying service worker:', error);
    }
  }
  
  // Fallback to service worker
  if (navigator.serviceWorker && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: count > 0 ? 'SET_BADGE' : 'CLEAR_BADGE',
      count: count
    });
    return true;
  }
  
  return false;
};

export const MessageProvider = ({ children }) => {
  const [client, setClient] = useState(null);
  const [data, setData] = useState(null);
  const [pushPermission, setPushPermission] = useState(('Notification' in window ? Notification.permission : null) || 'default');
  const [isPushSubscribed, setIsPushSubscribed] = useState(false);
  const [notificationRefreshTrigger, setNotificationRefreshTrigger] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Use refs to avoid stale closures in callbacks
  const pushPermissionRef = useRef(pushPermission);
  const isPushSubscribedRef = useRef(isPushSubscribed);
  const clientRef = useRef(client);
  const unreadCountRef = useRef(unreadCount);
  
  // Keep refs in sync with state
  useEffect(() => {
    pushPermissionRef.current = pushPermission;
  }, [pushPermission]);
  
  useEffect(() => {
    isPushSubscribedRef.current = isPushSubscribed;
  }, [isPushSubscribed]);
  
  useEffect(() => {
    clientRef.current = client;
  }, [client]);

  useEffect(() => {
    unreadCountRef.current = unreadCount;
  }, [unreadCount]);

  // Update app badge when unread count changes
  useEffect(() => {
    updateAppBadge(unreadCount);
  }, [unreadCount]);
  
  // Function to set badge count (called from notification components)
  const setBadgeCount = useCallback((count) => {
    const newCount = Math.max(0, count);
    setUnreadCount(newCount);
  }, []);

  // Function to increment badge count
  const incrementBadgeCount = useCallback(() => {
    setUnreadCount(prev => prev + 1);
  }, []);

  // Function to decrement badge count
  const decrementBadgeCount = useCallback(() => {
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, []);

  // Function to clear badge
  const clearBadge = useCallback(() => {
    setUnreadCount(0);
  }, []);
  
  // Trigger notification list refresh (for components to use)
  const triggerNotificationRefresh = useCallback(() => {
    setNotificationRefreshTrigger(prev => prev + 1);
  }, []);

  // Initialize push notifications
  const initializePushNotifications = async (username) => {
    try {
      // Check browser support
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        console.warn('Push notifications not supported in this browser');
        return;
      }

      // Initialize push notification service
      await pushNotificationService.initialize();

      // Check current permission
      if (!('Notification' in window)) {
        console.warn('Notification API not supported in this browser (e.g. iOS Safari < 16.4)');
        return;
      }
      let permission = Notification.permission;
      if (permission === 'default') {
        permission = await Notification.requestPermission();
      }
      setPushPermission(permission);

      if (permission === 'granted') {
        // Subscribe to push notifications (will validate and refresh if needed)
        const subscription = await pushNotificationService.subscribeToPushNotifications();
        setIsPushSubscribed(!!subscription);

        // Setup periodic refresh for mobile devices (every 30 minutes)
        pushNotificationService.setupPeriodicRefresh(30);

        console.log('Push notifications initialized successfully');
      } else {
        console.warn('Push notification permission:', permission);
      }
    } catch (error) {
      console.error('Error initializing push notifications:', error);
    }
  };

  // Refresh push subscription (for mobile when app becomes active)
  const refreshPushSubscription = useCallback(async () => {
    if (isPushSubscribedRef.current && pushPermissionRef.current === 'granted') {
      try {
        console.log('Refreshing push subscription...');
        await pushNotificationService.validateAndRefreshSubscription();
      } catch (error) {
        console.error('Error refreshing push subscription:', error);
      }
    }
  }, []);

  const initializeRabbitMQ = (username) => {
    // Deactivate existing client if any
    if (client) {
      client.deactivate();
    }

    const newClient = new Client({
      brokerURL: brokerURL,
      connectHeaders: {
        login: rabbit_username,
        passcode: rabbit_password,
      },
      reconnectDelay: 5000,
      onConnect: () => {
        //console.log("Connected to RabbitMQ");

        const consumerId = `consumer_${username}_${Date.now()}`;

        // Subscribe to user notifications
        newClient.subscribe(
          `/exchange/user_notifications_exchange/user.${username}.notifications`,
          (message) => {
            try {
              if (message.body) {
                const parsedMessage = JSON.parse(message.body);

                // Update local state - this triggers notification list updates
                setData(parsedMessage);

                // Show browser notification if push notifications are enabled
                // Use refs to get current values (avoid stale closures)
                if (parsedMessage.type === 'notification' &&
                  parsedMessage.notification &&
                  pushPermissionRef.current === 'granted' &&
                  isPushSubscribedRef.current) {

                  const notification = parsedMessage.notification;

                  // Send to service worker via message for push notification
                  if (navigator.serviceWorker && navigator.serviceWorker.controller) {
                    navigator.serviceWorker.controller.postMessage({
                      type: 'NEW_NOTIFICATION',
                      notification: {
                        title: 'Freecomers',
                        body: sanitizeNotificationBody(notification.content),
                        icon: notification.icon || '/logo192.png',
                        data: {
                          url: notification.redirect_link || '/notification',
                          notificationId: notification.id,
                          type: notification.notification_type,
                          timestamp: new Date().toISOString()
                        },
                        tag: `notification_${notification.id}`,
                        requireInteraction: notification.notification_type === 'new_message'
                      }
                    });
                  }
                }

                message.ack();
              }
            } catch (error) {
              console.error("Error processing message:", error);
            }
          },
          { ack: "client", id: consumerId }
        );

        // Subscribe to push notification status updates
        newClient.subscribe(
          `/exchange/user_notifications_exchange/user.${username}.push.status`,
          (message) => {
            try {
              if (message.body) {
                const statusUpdate = JSON.parse(message.body);
                if (statusUpdate.type === 'push_status_update') {
                  setIsPushSubscribed(statusUpdate.subscribed);
                }
              }
            } catch (error) {
              console.error("Error processing push status message:", error);
            }
          },
          { ack: "client", id: `push_status_${consumerId}` }
        );

        //console.log(`Subscribed to user notifications for ${username}`);
      },
      onStompError: (error) => {
        console.error("STOMP Error:", error);

        // Attempt reconnection with exponential backoff
        setTimeout(() => {
          if (username) {
            initializeRabbitMQ(username);
          }
        }, 10000);
      },
      onWebSocketClose: () => {
        //console.warn("WebSocket connection closed.");

        // Attempt reconnection
        setTimeout(() => {
          if (username) {
            initializeRabbitMQ(username);
          }
        }, 5000);
      },
      onDisconnect: () => {
        //console.log("Disconnected from RabbitMQ");
      },
    });

    newClient.activate();
    setClient(newClient);
  };

  // Helper method to sanitize notification body for browser notifications
  const sanitizeNotificationBody = (body) => {
    // Remove HTML tags
    const div = document.createElement('div');
    div.innerHTML = body;
    let text = div.textContent || div.innerText || '';

    // Decode HTML entities
    text = text.replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");

    // Trim and limit length
    text = text.trim();
    if (text.length > 200) {
      text = text.substring(0, 197) + '...';
    }

    return text;
  };

  // Unsubscribe from push notifications
  const unsubscribeFromPushNotifications = async () => {
    try {
      await pushNotificationService.unsubscribeFromPushNotifications();
      setIsPushSubscribed(false);
      console.log('Unsubscribed from push notifications');
    } catch (error) {
      console.error('Error unsubscribing from push notifications:', error);
    }
  };

  // Check push subscription status
  const checkPushSubscriptionStatus = async () => {
    try {
      const status = await pushNotificationService.checkSubscriptionStatus();
      setIsPushSubscribed(status.subscribed);
      return status;
    } catch (error) {
      console.error('Error checking push subscription status:', error);
      return { subscribed: false, supported: false };
    }
  };

  // Send test push notification
  const sendTestPushNotification = async () => {
    try {
      const result = await pushNotificationService.sendTestNotification();
      return result;
    } catch (error) {
      console.error('Error sending test push notification:', error);
      throw error;
    }
  };

  useEffect(() => {
    return () => {
      if (client) {
        client.deactivate();
      }
    };
  }, [client]);

  // Check push notification permission on mount
  useEffect(() => {
    if ('Notification' in window) {
      setPushPermission(Notification.permission);

      // Listen for permission changes
      if ('permissions' in navigator) {
        navigator.permissions.query({ name: 'notifications' })
          .then((permissionStatus) => {
            permissionStatus.onchange = () => {
              setPushPermission(permissionStatus.state);
            };
          });
      }
    }

    // Handle visibility change - refresh subscription when app becomes active
    // This is important for mobile devices where subscriptions can expire
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && isPushSubscribedRef.current) {
        console.log('App became visible, validating push subscription...');
        try {
          await pushNotificationService.validateAndRefreshSubscription();
        } catch (error) {
          console.error('Error refreshing subscription on visibility change:', error);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Handle online event - refresh subscription when network comes back
    const handleOnline = async () => {
      if (isPushSubscribedRef.current) {
        console.log('Network online, validating push subscription...');
        try {
          await pushNotificationService.validateAndRefreshSubscription();
        } catch (error) {
          console.error('Error refreshing subscription on online event:', error);
        }
      }
    };

    window.addEventListener('online', handleOnline);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  // Listen for messages from service worker (push notifications)
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handleServiceWorkerMessage = (event) => {
      if (event.data) {
        // Handle push notification received while app is open
        if (event.data.type === 'PUSH_RECEIVED') {
          console.log('Push notification received from service worker:', event.data);
          
          // Create a notification object similar to RabbitMQ format
          const pushData = event.data.notification;
          if (pushData) {
            setData({
              type: 'notification',
              notification: {
                id: pushData.data?.notificationId || Date.now(),
                content: pushData.body,
                notification_type: pushData.data?.type || 'push',
                redirect_link: pushData.data?.url || '/notification',
                icon: pushData.icon,
                is_read: 0,
                is_deleted: 0,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
              },
              timestamp: new Date().toISOString(),
              source: 'push'
            });
            
            // Also trigger a refresh to fetch the full notification from API
            triggerNotificationRefresh();
          }
        }
        
        // Handle notification click - refresh notification list
        if (event.data.type === 'NOTIFICATION_CLICKED') {
          console.log('Notification clicked:', event.data);
          triggerNotificationRefresh();
        }
      }
    };

    navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);

    return () => {
      navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
    };
  }, [triggerNotificationRefresh]);

  const value = {
    initializeRabbitMQ,
    data,
    initializePushNotifications,
    unsubscribeFromPushNotifications,
    checkPushSubscriptionStatus,
    sendTestPushNotification,
    refreshPushSubscription,
    pushPermission,
    isPushSubscribed,
    pushSupported: 'serviceWorker' in navigator && 'PushManager' in window,
    notificationRefreshTrigger,
    triggerNotificationRefresh,
    // Badge management
    unreadCount,
    setBadgeCount,
    incrementBadgeCount,
    decrementBadgeCount,
    clearBadge
  };

  return (
    <MessageContext.Provider value={value}>
      {children}
    </MessageContext.Provider>
  );
};

export const useMessageContext = () => useContext(MessageContext);